import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Celebrity } from "../../lib/types";

interface SeatSlotProps {
  celebrity?: Celebrity | null;
  readonly: boolean;
  displayMode?: boolean;
  onAdd?: () => void;
  onRemove?: () => void;
}

const cardBase =
  "w-24 h-36 sm:w-36 sm:h-48 rounded-xl flex flex-col items-center justify-center gap-2";

function CelebrityImage({
  celebrity,
  textSize,
}: {
  celebrity: Celebrity;
  textSize: string;
}) {
  const [errored, setErrored] = useState(false);

  if (celebrity.image_url && !errored) {
    return (
      <img
        src={celebrity.image_url}
        alt={celebrity.name}
        className="w-full h-full object-cover object-top"
        referrerPolicy="no-referrer"
        onError={() => setErrored(true)}
      />
    );
  }

  return (
    <div className="w-full h-full bg-stone-200 dark:bg-stone-800 flex items-center justify-center">
      <span className={`${textSize} font-bold text-stone-400`}>
        {celebrity.name[0]}
      </span>
    </div>
  );
}

export function SeatSlot({
  celebrity,
  readonly,
  displayMode,
  onAdd,
  onRemove,
}: SeatSlotProps) {
  const { t } = useTranslation();

  if (celebrity && displayMode) {
    return (
      <div className="w-24 h-36 sm:w-36 sm:h-48 rounded-xl overflow-hidden relative shadow-xl ring-1 ring-black/20">
        <CelebrityImage celebrity={celebrity} textSize="text-5xl" />
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-6 pb-2 px-2">
          <p className="text-white text-xs font-semibold text-center leading-tight">
            {celebrity.name}
          </p>
        </div>
      </div>
    );
  }

  if (celebrity) {
    return (
      <div
        className={`w-24 h-36 sm:w-36 sm:h-48 rounded-xl overflow-hidden relative shadow-md ring-1 ring-black/10 ${!readonly ? "hover:ring-2 hover:ring-amber-500/60 transition-all" : ""}`}
      >
        <CelebrityImage celebrity={celebrity} textSize="text-4xl" />
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-6 pb-2 px-2">
          <p className="text-white text-xs font-semibold text-center leading-tight">
            {celebrity.name}
          </p>
        </div>
        {!readonly && (
          <button
            type="button"
            onClick={onRemove}
            className="absolute top-2 right-2 w-5 h-5 rounded-full bg-black/40 hover:bg-red-600 text-white text-xs flex items-center justify-center transition-colors cursor-pointer"
            aria-label={t("seat.remove", { name: celebrity.name })}
          >
            ✕
          </button>
        )}
      </div>
    );
  }

  if (!readonly) {
    return (
      <button
        type="button"
        onClick={onAdd}
        className={`${cardBase} group border-2 border-dashed border-stone-300 dark:border-stone-700 hover:border-amber-500 text-stone-400 dark:text-stone-600 hover:text-amber-500 transition-colors cursor-pointer`}
      >
        <span className="text-2xl sm:text-3xl leading-none group-hover:scale-110 transition-transform">
          +
        </span>
        <span className="text-xs hidden sm:block">{t("seat.add")}</span>
      </button>
    );
  }

  return (
    <div
      className={`${cardBase} border-2 border-dashed border-stone-200 dark:border-stone-800 text-stone-400 dark:text-stone-700`}
    >
      <span className="text-xs">{t("seat.empty")}</span>
    </div>
  );
}
